// English translations for the core application areas
export const enTranslations = {
  common: {
    // Hero section
    "hero.title": "Control Your Contracts",
    "hero.subtitle": "Simplify contract management with our powerful solution. Get full overview, reduce costs and never miss another expiration.",
    "hero.companyTitle": "Stop Losing Money on Forgotten Contracts",
    "hero.companySubtitle": "Subtrackr gives you complete control over all your contracts. Get automatic reminders, track expenses, and never miss a renewal deadline again.",
    "hero.badge": "🎉 Free forever plan available",
    "hero.benefit1": "Never miss another renewal deadline",
    "hero.benefit2": "Track all contract costs in one place",
    "hero.benefit3": "Save 15% on average contract costs",
    "hero.getStarted": "Start Free Today",
    "hero.watchDemo": "See How It Works",
    "hero.neverMiss": "Never miss an expiration",
    "hero.trustBadge1": "GDPR Compliant",
    "hero.trustBadge2": "Free Plan Available",
    "hero.reminderCard": "Reminder sent!",
    "hero.reminderCardSub": "Contract expires in 30 days",

    // Stats section - Product benefits, not fake customer stats
    "stats.contractsValue": "∞",
    "stats.contractsLabel": "Unlimited contracts",
    "stats.remindersValue": "30+",
    "stats.remindersLabel": "Days advance notice",
    "stats.securityValue": "100%",
    "stats.securityLabel": "Secure & GDPR compliant",
    "stats.setupValue": "2 min",
    "stats.setupLabel": "Quick setup",

    // How It Works section
    "howItWorks.badge": "Simple to Get Started",
    "howItWorks.title": "How It Works",
    "howItWorks.subtitle": "Get started in minutes and take control of your contracts",
    "howItWorks.step1.title": "Add Your Contracts",
    "howItWorks.step1.description": "Upload PDFs or enter contract details manually. Our AI can extract key information automatically.",
    "howItWorks.step2.title": "Get Smart Reminders",
    "howItWorks.step2.description": "Receive email notifications before renewals and deadlines. Never miss an important date again.",
    "howItWorks.step3.title": "Save Time & Money",
    "howItWorks.step3.description": "Track costs, spot savings opportunities, and make informed decisions with powerful analytics.",

    // Features section
    "features.badge": "Powerful Features",
    "features.title": "Everything You Need to Take Control",
    "features.subtitle": "From basic contract tracking to AI-powered insights, Subtrackr has all the tools you need",
    "features.cta": "See All Features",
    
    "features.contractManagement.title": "Contract Management",
    "features.contractManagement.description": "Store, organize, and search all your contracts in one secure place. Never lose track of important documents again.",
    
    "features.reminders.title": "Smart Reminders",
    "features.reminders.description": "Get automatic email notifications before renewals and deadlines. Stop paying for contracts you don't need.",
    
    "features.dashboards.title": "Powerful Analytics",
    "features.dashboards.description": "Visual dashboards showing contract status, costs, and trends. Make data-driven decisions about your contracts.",
    
    "features.aiPowered.title": "AI-Powered Insights",
    "features.aiPowered.description": "Let AI analyze your contracts for cost optimization opportunities and risk assessment. Extract data from PDFs automatically.",
    
    "features.integrations.title": "ERP Integrations",
    "features.integrations.description": "Connect with Tripletex, Finago, and other systems. Auto-sync expenses and detect recurring payments.",
    
    "features.dataHealth.title": "Data Health Monitoring",
    "features.dataHealth.description": "Track data quality across all contracts. Get recommendations to improve your contract database.",

    // Pricing section
    "pricing.title": "Simple, Transparent Pricing",
    "pricing.subtitle": "Choose the plan that fits your needs. Upgrade or downgrade anytime.",
    "pricing.perMonth": "/month",
    "pricing.mostPopular": "Most Popular",
    "pricing.question": "Need help deciding?",
    "pricing.contactUs": "Contact us",
    "pricing.customPlan.title": "Build Your Own Plan",
    "pricing.customPlan.description": "None of our standard plans fit your needs? Create a fully customized subscription by selecting only the features you need. Pay only for what you use.",
    "pricing.customPlan.badge1": "Flexible pricing",
    "pricing.customPlan.badge2": "Pick & choose features",
    "pricing.customPlan.badge3": "Modify anytime",
    "pricing.customPlan.button": "Create Custom Plan",
    
    "customPlan.signUpToContinue": "Sign up to continue",
    "customPlan.invalidPlanData": "Invalid plan data. Please try again.",
    "customPlan.preparingCheckout": "Preparing Checkout",
    "customPlan.redirectingToPayment": "Redirecting you to secure payment...",
    "customPlan.planCreated": "Your custom plan has been created!",
    "customPlan.testActivation": "Test Activation",
    "customPlan.enterTestCode": "Enter test code",
    "customPlan.activate": "Activate",
    "customPlan.invalidTestCode": "Invalid test code",
    "customPlan.selectFeaturesFirst": "Please select at least one feature",
    "customPlan.loginRequired": "Please log in to activate your subscription",
    "customPlan.testActivationSuccess": "Test subscription activated successfully!",
    "customPlan.activationFailed": "Failed to activate subscription",
    
    // Plan Selection page
    "planSelection.title": "Choose Your Plan",
    "planSelection.subtitle": "Select a plan to get started with Subtrackr",
    "planSelection.selectPlan": "Select Plan",
    "planSelection.haveAccount": "Already have an account?",
    
    "pricing.starter.name": "Starter",
    "pricing.starter.price": "999 kr",
    "pricing.starter.description": "Perfect for small businesses",
    "pricing.starter.feature1": "Up to 50 contracts",
    "pricing.starter.feature2": "Email notifications",
    "pricing.starter.feature3": "Basic reporting",
    "pricing.starter.feature4": "24/7 support",
    "pricing.starter.button": "Get Started",
    
    "pricing.professional.name": "Professional",
    "pricing.professional.price": "1,999 kr",
    "pricing.professional.description": "Ideal for growing businesses",
    "pricing.professional.feature1": "Unlimited contracts",
    "pricing.professional.feature2": "Advanced dashboards",
    "pricing.professional.feature3": "Team collaboration",
    "pricing.professional.feature4": "Custom notifications",
    "pricing.professional.feature5": "Priority support",
    "pricing.professional.button": "Start Free Trial",
    
    "pricing.enterprise.name": "Enterprise",
    "pricing.enterprise.price": "3,999 kr",
    "pricing.enterprise.description": "For large organizations",
    "pricing.enterprise.feature1": "Unlimited everything",
    "pricing.enterprise.feature2": "Custom integrations",
    "pricing.enterprise.feature3": "Dedicated account manager",
    "pricing.enterprise.feature4": "SLA guarantees",
    "pricing.enterprise.feature5": "Advanced security features",
    "pricing.enterprise.feature6": "On-premises option",
    "pricing.enterprise.button": "Contact Sales",
    
    // Register page
    "register.selectedPlan": "Selected plan",
    
    // CTA section
    "cta.title": "Ready to Take Control of Your Contracts?",
    "cta.subtitle": "Join hundreds of Norwegian businesses saving time and money with smarter contract management",
    "cta.getStarted": "Start Free Today",
    "cta.contactSales": "Contact Sales",
    "cta.benefit1": "Free forever plan",
    "cta.benefit2": "No credit card required",
    "cta.benefit3": "Setup in 2 minutes",
    "cta.trustNote": "No credit card required · Free plan available · Setup in minutes",
    
    // Footer
    "footer.description": "Subtrackr helps businesses take control of their contracts with powerful management tools, reminders, and analytics.",
    "footer.product": "Product",
    "footer.features": "Features",
    "footer.pricing": "Pricing",
    "footer.security": "Security",
    "footer.company": "Company",
    "footer.about": "About",
    "footer.contact": "Contact",
    "footer.privacy": "Privacy Policy",
    "footer.terms": "EULA",
    "footer.copyright": "© {{year}} Subtrackr. All rights reserved.",

    // EULA
    "eula.title": "End User License Agreement (EULA)",
    "eula.lastUpdated": "Last updated: February 2026",
    "eula.effectiveDate": "Effective date: February 25, 2026",
    "eula.contactText": "If you have any questions about this End User License Agreement, please contact us at legal@subtrackr.com",
    "eula.modal.title": "End User License Agreement",
    "eula.modal.description": "Please review and accept our End User License Agreement to continue using Subtrackr.",
    "eula.modal.summary1": "By accepting this EULA, you agree to the terms governing your use of the Subtrackr platform, including license restrictions, data ownership, payment obligations, and liability limitations.",
    "eula.modal.summary2": "You retain ownership of all data you upload to the platform. Subtrackr retains all intellectual property rights to the platform itself.",
    "eula.modal.summary3": "This agreement is governed by Norwegian law. The full agreement is available for your review.",
    "eula.modal.checkboxLabel": "I have read and agree to the",
    "eula.modal.eulaLink": "End User License Agreement",
    "eula.modal.accept": "Accept & Continue",
    "eula.modal.accepting": "Accepting...",
    "eula.acceptError": "Failed to record acceptance. Please try again.",
    "register.eulaAgree": "I have read and agree to the",
    "register.eulaLink": "End User License Agreement",
    "register.creating": "Creating account...",
    "register.createAccount": "Create account",
    
    // Navigation
    "goToDashboard": "Go to Dashboard",
    "login": "Log In",
    "signup": "Sign Up",

    // Timeline translations
    "viewTimeline": "View Timeline",
    "timeline": {
      "title": "Timeline",
      "subtitle": "View the history of changes made to this contract",
      "noChanges": "No changes recorded for this contract yet.",
      "loading": "Loading timeline..."
    },
    
    // Contract Types translations
    contractTypes: {
      subscription: {
        name: "Subscription",
        description: "A recurring contract with regular payments"
      },
      serviceAgreement: {
        name: "Service Agreement",
        description: "Contract for providing services"
      },
      license: {
        name: "License Agreement",
        description: "Contract for the use of intellectual property"
      },
      onetime: {
        name: "One-time Purchase",
        description: "Single purchase without renewal"
      },
      lease: {
        name: "Lease/Rental",
        description: "Contract for renting/leasing property or equipment"
      },
      sales: {
        name: "Sales Contract",
        description: "Contract for selling products or services"
      },
      employment: {
        name: "Employment Contract",
        description: "Contract with employees"
      },
      intro: "Contract types help categorize your agreements and apply consistent rules. Each type can have specific renewal settings and direction/purpose.",
      empty: "You don't have any contract types yet. Add default types or create your own.",
      addingDefaults: "Adding defaults...",
      addDefaults: "Add Default Contract Types",
      createManually: "Create Type Manually",
      yourTypes: "Your Contract Types",
      count: "You have {{count}} contract type(s)"
    },

    // Categories translations
    categories: {
      software: {
        name: "Software",
        description: "Software licenses and subscriptions"
      },
      hardware: {
        name: "Hardware",
        description: "Physical hardware and equipment"
      },
      services: {
        name: "Services",
        description: "Professional and consulting services"
      },
      utilities: {
        name: "Utilities",
        description: "Utilities like electricity, water, etc."
      },
      rent: {
        name: "Rent",
        description: "Office and workspace rentals"
      },
      intro: "Categories help you organize your contracts and filter them more effectively. Add some categories that match your organization's needs.",
      noCategories: "No Categories Yet",
      empty: "You haven't created any categories yet. Add some to help organize your contracts.",
      addDefaults: "Add Default Categories",
      createCustom: "Create Custom Category"
    }
  }
};
